import React, { useEffect, useState } from 'react';
import { Loader2, Layers, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';

export const C = { navy: '#0D1B2A', blue: '#1A4C7C', green: '#1D9E75', red: '#E24B4A', amber: '#B27516', slate: '#5C6B7A', bg: '#F5F7FA' };

export const confidenceStyle = c => c === 'high' ? { background: 'rgba(29,158,117,0.12)', color: C.green }
  : c === 'medium' ? { background: 'rgba(178,117,22,0.12)', color: C.amber }
    : c === 'low' ? { background: 'rgba(26,76,124,0.12)', color: C.blue }
      : c === 'not_available' ? { background: 'rgba(92,107,122,0.12)', color: C.slate }
        : { background: 'rgba(226,75,74,0.1)', color: C.red };

export const fmtValue = v => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  if (typeof v === 'number') return v.toLocaleString();
  if (typeof v === 'object') {
    if ('min_m' in v) return v.max_m ? `${v.min_m / 1000}–${v.max_m / 1000} km` : `> ${v.min_m / 1000} km`;
    if ('approx_m' in v) return `≈ ${(v.approx_m / 1000).toFixed(0)} km`;
    if ('code' in v || 'label' in v) return [v.code, v.label].filter(Boolean).join(' · ');
    if ('prone' in v) return `${v.prone ? 'prone' : 'not prone'}${v.band && v.band !== 'none' ? ` · ${v.band}` : ''}${v.category ? ` · category ${v.category}` : ''}`;
    return JSON.stringify(v);
  }
  return String(v);
};

export const SourcedRow = ({ name, s, testid }) => (
  <div className="grid grid-cols-12 gap-2 text-[11px] border-t border-[rgba(136,150,168,0.1)] py-1.5 items-start" data-testid={testid}>
    <span className="col-span-4" style={{ color: C.slate }}>{name.replace(/_/g, ' ')}</span>
    <span className="col-span-3 font-mono break-words" style={{ color: C.navy }} data-testid={`${testid}-value`}>{fmtValue(s?.value)}</span>
    <span className="col-span-5 flex items-start gap-1.5">
      <span className="text-[9px] font-semibold px-1 py-0.5 rounded uppercase shrink-0" style={confidenceStyle(s?.confidence)} data-testid={`${testid}-confidence`}>{(s?.confidence || '').replace('_', ' ')}</span>
      <span className="text-[10px] leading-tight line-clamp-2" style={{ color: C.slate }} title={s?.source}>{s?.source}</span>
    </span>
  </div>
);

export const PanelShell = ({ title, icon, meta, children, testid }) => (
  <div className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white" data-testid={testid}>
    <div className="px-3 py-2 flex items-center justify-between gap-2" style={{ background: C.bg }}>
      <span className="text-[10px] font-semibold uppercase tracking-wider flex items-center gap-1.5" style={{ color: C.navy }}>{icon}{title}</span>
      <span className="text-[9px] text-right" style={{ color: C.slate }}>{meta}</span>
    </div>
    <div className="px-3 pb-3">{children}</div>
  </div>
);

export const Loading = ({ what }) => (
  <p className="py-4 text-[11px] flex items-center gap-1.5" style={{ color: C.slate }}><Loader2 className="w-3.5 h-3.5 animate-spin" /> asking the state layers for {what}…</p>
);

export const Degraded = ({ items, testid }) => items?.length ? (
  <p className="mt-2 text-[10px] flex items-start gap-1" style={{ color: C.amber }} data-testid={testid}>
    <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> a layer did not answer, so the field says so instead of guessing: {items.join(' · ')}
  </p>
) : null;

export const useFetch = (api, url, deps) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let live = true;
    setData(null); setErr(null);
    if (!url) return undefined;
    api.get(url).then(({ data: d }) => live && setData(d))
      .catch(e => { console.error('API error:', e?.message); live && setErr(e?.response?.data?.detail || e?.message); });
    return () => { live = false; };
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
  return [data, err];
};

export const Collapsible = ({ label, count, children, testid }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-2">
      <button type="button" onClick={() => setOpen(o => !o)} className="text-[10px] flex items-center gap-1" style={{ color: C.blue }} data-testid={testid}>
        {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />} {open ? 'hide' : 'show'} {count} {label}
      </button>
      {open && children}
    </div>
  );
};

export const LayerFooter = ({ layers, testid }) => layers?.length ? (
  <p className="mt-2 text-[9px] flex flex-wrap gap-x-2 gap-y-0.5 items-center" style={{ color: C.slate }} data-testid={testid}>
    <Layers className="w-3 h-3" />
    {layers.map(l => <span key={l.code}>{l.code} <span style={{ color: l.status === 'unavailable' ? C.red : l.status === 'hit' ? C.green : C.slate }}>{l.status}</span>{l.elapsed_ms ? ` ${l.elapsed_ms}ms` : ''}</span>)}
  </p>
) : null;
